import React, { useState, useRef, useEffect } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  List,
  ListItem,
  Input,
  InputProps,
  useDisclosure,
  useColorModeValue,
  Text,
  Flex,
  Portal
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

interface CustomDropdownProps extends Omit<InputProps, 'onChange'> {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select or type...',
  ...inputProps
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes((inputValue || '').toLowerCase())
  );

  const exactMatch = options.find(o => o.toLowerCase() === (inputValue || '').toLowerCase());
  const showCreateOption = (inputValue || '').trim() !== '' && !exactMatch;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onChange(e.target.value);
    if (!isOpen) onOpen();
  };

  const handleSelect = (option: string) => {
    setInputValue(option);
    onChange(option);
    onClose();
  };

  const bg = useColorModeValue('white', '#111827');
  const hoverBg = useColorModeValue('rgba(16, 185, 129, 0.08)', 'rgba(16, 185, 129, 0.12)');
  const borderColor = useColorModeValue('gray.200', '#374151');

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      initialFocusRef={inputRef}
      placement="bottom-start"
      autoFocus={false}
      matchWidth
    >
      <PopoverTrigger>
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onClick={onOpen}
          onFocus={onOpen}
          placeholder={placeholder}
          _focus={{ borderColor: '#0ea5e9', boxShadow: 'none' }}
          _focusVisible={{ outline: 'none' }}
          {...inputProps}
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          bg={bg}
          border="1px solid"
          borderColor={borderColor}
          shadow="xl"
          borderRadius="xl"
          overflow="hidden"
          _focus={{ outline: 'none' }}
          _focusVisible={{ outline: 'none' }}
          w="100%"
          p={1}
        >
          <List py={1} maxH="250px" overflowY="auto" w="100%">
            {filteredOptions.map((option) => (
              <ListItem
                key={option}
                px={3}
                py={2}
                borderRadius="md"
                cursor="pointer"
                fontSize="xs"
                color={useColorModeValue('gray.800', 'gray.200')}
                _hover={{ bg: hoverBg, color: '#0ea5e9' }}
                onClick={() => handleSelect(option)}
                fontWeight={inputValue === option ? '700' : '500'}
                transition="all 0.15s ease"
              >
                {option}
              </ListItem>
            ))}
            {showCreateOption && (
              <ListItem
                px={3}
                py={2}
                borderRadius="md"
                cursor="pointer"
                _hover={{ bg: 'rgba(14, 165, 233, 0.12)', color: '#0284c7' }}
                onClick={() => handleSelect(inputValue)}
                transition="all 0.15s ease"
                color="#0284c7"
                fontWeight="700"
                fontSize="xs"
              >
                <Flex align="center" gap={2}>
                  <FontAwesomeIcon icon={faPlus} size="xs" />
                  <Text>Add "{inputValue}"</Text>
                </Flex>
              </ListItem>
            )}
            {filteredOptions.length === 0 && !showCreateOption && (
              <ListItem px={3} py={2} color="gray.500" fontStyle="italic" fontSize="xs">
                Type to create a new organization...
              </ListItem>
            )}
          </List>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default CustomDropdown;
